/** @format */

import { apiClient } from "./api";
import { ENDPOINTS } from "./constants";
import { BolUploadError } from "@/utils/bolUploadUtils";

function parseBolUploadError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail ?? data;

  if (status === 401) {
    return new BolUploadError("Please sign in to upload a BOL.", { code: "UNAUTHORIZED" });
  }
  if (status === 413) {
    return new BolUploadError("PDF must be 10MB or smaller.", { code: "FILE_TOO_LARGE" });
  }

  const message =
    detail?.message ??
    detail?.aguiEvent?.data?.message ??
    data?.message ??
    "Unable to process this BOL document.";

  const notFound =
    status === 404 ||
    /not found|no matching|unmatched|could not match/i.test(String(message)) ||
    detail?.tracking?.shipmentMatch?.matched === false;

  return new BolUploadError(message, {
    code: detail?.code ?? "BOL_UPLOAD_FAILED",
    notFound,
  });
}

function pickNumericTotal(...values) {
  for (const v of values) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

function resolveShipmentListPayload(data) {
  let payload = data;
  if (data?.data != null && typeof data.data === "object" && !Array.isArray(data.data)) {
    payload = data.data;
    if (
      !Array.isArray(payload?.shipments) &&
      payload?.data != null &&
      typeof payload.data === "object" &&
      !Array.isArray(payload.data)
    ) {
      payload = payload.data;
    }
  }
  return payload ?? {};
}

function resolveShipmentListTotal(envelope, payload, shipments, requestedLimit) {
  const total = pickNumericTotal(
    payload?.total,
    payload?.totalCount,
    payload?.total_count,
    payload?.count,
    payload?.pagination?.total,
    payload?.meta?.total,
    envelope?.total,
    envelope?.totalCount,
    envelope?.meta?.total,
  );
  if (total !== undefined) return total;
  const len = shipments?.length ?? 0;
  if (requestedLimit != null && len > 0 && len < requestedLimit) return len;
  return undefined;
}

/**
 * View All when the API reports more than the preview cap, or a probe fetch
 * returned more rows than we display (handles total === page size from API).
 */
export function computeCanViewAll(total, fetchedCount, previewLimit = 3) {
  if (fetchedCount > previewLimit) return true;
  if (total != null && total !== "") {
    const n = Number(total);
    if (Number.isFinite(n) && n > previewLimit) return true;
  }
  return false;
}

/** API envelope: { status, message, data: { shipments, total, ... } } */
function unwrapShipmentListResponse(data, requestedLimit) {
  const payload = resolveShipmentListPayload(data);
  const shipments = Array.isArray(payload?.shipments) ? payload.shipments : [];
  const reportedTotal = resolveShipmentListTotal(data, payload, shipments, requestedLimit);
  const fetched = shipments.length;
  const total =
    reportedTotal != null ? Math.max(reportedTotal, fetched) : fetched;

  return {
    shipments,
    total,
    reportedTotal,
    hasMore: fetched > (requestedLimit ?? fetched) || (reportedTotal != null && fetched < reportedTotal),
    limit: payload?.limit ?? requestedLimit,
    offset: payload?.offset ?? 0,
  };
}

export const trackingService = {
  /**
   * @param {number} [limit=10]
   * @returns {Promise<{ shipments: object[], total: number, limit: number }>}
   */
  getActiveShipments: async (limit = 10) => {
    const { data } = await apiClient.get(ENDPOINTS.TRACKING.ACTIVE, {
      params: { limit },
    });
    return unwrapShipmentListResponse(data, limit);
  },

  /**
   * @param {number} [limit=10]
   * @param {number} [offset=0]
   * @returns {Promise<{ shipments: object[], total: number, limit: number, offset: number }>}
   */
  getTrackedHistory: async (limit = 10, offset = 0) => {
    const { data } = await apiClient.get(ENDPOINTS.TRACKING.TRACKED_HISTORY, {
      params: { limit, offset },
    });
    return unwrapShipmentListResponse(data, limit);
  },

  /**
   * @param {File} file
   * @param {{ shipmentId?: string, bookingId?: string, sessionId?: string }} [overrides]
   */
  uploadBol: async (file, overrides = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    if (overrides.shipmentId) formData.append("shipment_id", overrides.shipmentId);
    if (overrides.bookingId) formData.append("booking_id", overrides.bookingId);
    if (overrides.sessionId) formData.append("session_id", overrides.sessionId);

    try {
      const { data } = await apiClient.post(ENDPOINTS.TRACKING.BOL_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data?.data ?? data;
    } catch (err) {
      throw parseBolUploadError(err);
    }
  },
};
